import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { UISystemIcon, UIWizardFlowHeader } from '@futrli/ui-components';

import { useConsolidationWizardContext } from '../../consolidation-wizard-context/consolidation-wizard-context';
import { useConsolidationContext } from '../../consolidation-context/consolidation-context';
import { WarningEliminationsGroupModal } from '../eliminations/elimination-modals/warning-eliminations-group-modal';
import { ELIMINATIONS_CHOICES } from '../../consolidation-wizard-context/types';

import wizardStyles from '../../create-consolidation/create-consolidation.scss';
import styles from './eliminations-question.scss';
import { UIOptionButton } from './ui-option-button';

export const EliminationsQuestion = () => {
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  const {
    eliminationsChoice,
    handleBack,
    handleNext,
    isBackEnabled,
    setEliminationsChoice,
    stepText
  } = useConsolidationWizardContext();

  const {
    eliminations: { eliminationGroups, handleRemoveAllEliminationGroups }
  } = useConsolidationContext();

  const handleChoiceClick = useCallback(
    (value?: string) => {
      setEliminationsChoice(value as ELIMINATIONS_CHOICES);
    },
    [setEliminationsChoice]
  );

  const handlePreNextClick = useCallback(() => {
    if (eliminationsChoice === ELIMINATIONS_CHOICES.NO && Object.keys(eliminationGroups).length > 0) {
      setShowWarningDialog(true);
    } else {
      handleNext();
    }
  }, [eliminationGroups, eliminationsChoice, handleNext]);

  const handleWarningModalSubmit = useCallback(() => {
    setShowWarningDialog(false);
    handleRemoveAllEliminationGroups();
    handleNext();
  }, [handleRemoveAllEliminationGroups, handleNext]);

  const handleWarningModalCancel = useCallback(() => {
    setShowWarningDialog(false);
  }, []);

  const doubleCheckIcon = <UISystemIcon iconName="double-check-onboarding" width={30} height={30} />;

  const buttons = (
    <div className={styles.buttons}>
      <UIOptionButton
        icon={eliminationsChoice === ELIMINATIONS_CHOICES.YES ? doubleCheckIcon : null}
        onClick={handleChoiceClick}
        selected={eliminationsChoice === ELIMINATIONS_CHOICES.YES}
        text={
          <FormattedMessage
            id="consolidation.yes_i_have_eliminations"
            defaultMessage="Yes, I have account eliminations"
          />
        }
        value={ELIMINATIONS_CHOICES.YES}
      />
      <UIOptionButton
        icon={eliminationsChoice === ELIMINATIONS_CHOICES.NO ? doubleCheckIcon : null}
        onClick={handleChoiceClick}
        selected={eliminationsChoice === ELIMINATIONS_CHOICES.NO}
        style={{ marginLeft: '20px' }}
        text={
          <FormattedMessage
            id="consolidation.no_take_me_to_the_next_step"
            defaultMessage="No, take me to the next step"
          />
        }
        value={ELIMINATIONS_CHOICES.NO}
      />
    </div>
  );

  return (
    <div className={styles.container}>
      <UIWizardFlowHeader
        disableBack={!isBackEnabled}
        disableNext={typeof eliminationsChoice === 'undefined'}
        extraContent={buttons}
        innerContainerClassname={clsx(wizardStyles.headerInner, styles.headerInner)}
        outerContainerClassname={wizardStyles.header}
        onBack={handleBack}
        onNext={handlePreNextClick}
        title={stepText?.title || ''}
        description={stepText?.desc || ''}
      />
      {showWarningDialog && (
        <WarningEliminationsGroupModal
          showModal={showWarningDialog}
          handleClose={handleWarningModalCancel}
          handleSubmit={handleWarningModalSubmit}
        />
      )}
    </div>
  );
};
